import { useAppStore } from '../stores/useAppStore';
import { chats as chatStore } from '../storage/db';
import type { ChatMessage } from '../types';

const WELCOME_TEXT = `Welcome — this is a fresh universe with nothing in it yet.

Before stories can be generated here, the universe needs some basics:

- **Who** the stories are about (characters, their temperaments, how they speak)
- **Where** the stories happen (setting, era, register)
- **Voice & feel** — how the stories should *sound* when read aloud
- **What to avoid** — anything off-limits for this universe (audience constraints, topical avoids, tone breaks)

I'll help you build these by listening and writing the answers into the universe's story bible as we go. You'll see each proposed edit and approve it before it sticks.

To start, tell me a bit about what you have in mind. Some prompts:

- Who's the universe for? Yourself, a particular child, an adult audience, a mix?
- Are there characters you already have in mind, or do you want to invent them together?
- Realist, fantastical, domestic, wild — what kind of setting?
- Any voice or feel you're going for? (Lyrical and warm? Spare and dry? Playful? Like a specific author?)

You don't have to answer all of these in one go — start anywhere it's interesting, and we'll fill in the rest as we talk.`;

/**
 * One-shot onboarding for a brand-new user (or anyone who has somehow ended
 * up with zero universes). Creates a placeholder universe, a chat tied to it,
 * pre-seeds the chat with a welcome message, and returns the IDs so the
 * caller can navigate.
 */
export async function startOnboarding(): Promise<{
  universeId: string;
  chatId: string;
}> {
  const store = useAppStore.getState();
  const universe = store.createUniverse({
    name: 'My Universe',
    defaultProtagonists: '',
  });
  store.setActiveUniverse(universe.id);

  const chat = store.createChat(universe.id, 'Universe setup');
  const welcome: ChatMessage = {
    role: 'assistant',
    content: [{ type: 'text', text: WELCOME_TEXT }],
    at: Date.now(),
  };
  try {
    await chatStore.set(chat.id, [welcome]);
  } catch (e) {
    console.warn('[onboarding] failed to seed welcome message', e);
  }

  return { universeId: universe.id, chatId: chat.id };
}
